import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import proj4 from "proj4";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowRight, ListChecks, Link2, X, Save, Link as LinkIcon, Wand2, Check, Zap, MapPin, BookMarked, Package } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { listFiles, listStandards, previewQA, runQA, suggestMatches, getLinkRules, saveLinkRules, getFile, downloadSubmissionPack, listSurfaces, listCategories, listToleranceProfiles, createToleranceProfile, deleteToleranceProfile } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import QAResult from "@/components/QAResult";

const mgaDef = (z) => `+proj=utm +zone=${z} +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`;

export default function QAPage() {
  const [files, setFiles] = useState([]);
  const [standards, setStandards] = useState([]);
  const [design, setDesign] = useState("");
  const [survey, setSurvey] = useState("");
  const [standardId, setStandardId] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qa, setQa] = useState(null);
  const [links, setLinks] = useState([]);
  const [selDesign, setSelDesign] = useState(null);
  const [bounds, setBounds] = useState(1.0);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [autoAccept, setAutoAccept] = useState(true);
  const [threshold, setThreshold] = useState(70);
  const [autoNote, setAutoNote] = useState(null);
  const [mapLink, setMapLink] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [surfaces, setSurfaces] = useState([]);
  const [volBase, setVolBase] = useState("none");
  const [volTop, setVolTop] = useState("none");
  const [perType, setPerType] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [typeOverrides, setTypeOverrides] = useState({});
  const [designTypes, setDesignTypes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("none");
  const [showOverrides, setShowOverrides] = useState(false);

  useEffect(() => {
    listFiles().then(setFiles);
    listSurfaces().then(setSurfaces);
    listCategories().then(setCategories);
    listToleranceProfiles().then(setProfiles);
    listStandards().then((s) => {
      setStandards(s);
      const def = s.find((x) => x.is_default) || s[0];
      if (def) setStandardId((prev) => prev || def.id);
    });
  }, []);

  const { user, isPro } = useAuth();
  const lgaLocked = !isPro && !!user?.lga_schema;
  const availableStandards = lgaLocked ? standards.filter((s) => s.id === user.lga_schema) : standards;

  useEffect(() => {
    if (lgaLocked) setStandardId(user.lga_schema);
  }, [lgaLocked, user?.lga_schema]);

  const activeStd = standards.find((s) => s.id === standardId);
  const { activeProject } = useProject();

  useEffect(() => {
    if (!activeProject) return;
    if (activeProject.design_file_id) setDesign(activeProject.design_file_id);
    if (activeProject.survey_file_id) setSurvey(activeProject.survey_file_id);
    if (activeProject.standard_id) setStandardId(activeProject.standard_id);
    if (activeProject.existing_surface_id) setVolBase(activeProject.existing_surface_id);
    if (activeProject.design_surface_id) setVolTop(activeProject.design_surface_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  useEffect(() => {
    if (!design) { setDesignTypes([]); return; }
    getFile(design).then((d) => {
      setDesignTypes(Array.from(new Set((d.assets || []).map((a) => a.type))).sort());
    }).catch(() => setDesignTypes([]));
  }, [design]);

  const mapOpts = () => {
    if (!perType) return {};
    const sm = {}; Object.entries(catMap).forEach(([k, v]) => { if (v && v !== "default") sm[k] = v; });
    const to = {}; Object.entries(typeOverrides).forEach(([k, v]) => { if (v && v !== "default") to[k] = v; });
    return { standard_map: Object.keys(sm).length ? sm : null, type_overrides: Object.keys(to).length ? to : null };
  };

  const applyProfile = (pid) => {
    setProfileId(pid);
    if (pid === "none") return;
    const p = profiles.find((x) => x.id === pid);
    if (!p) return;
    if (p.default_standard_id) setStandardId(p.default_standard_id);
    setCatMap(p.categories || {});
    setTypeOverrides(p.types || {});
    setPerType(true);
    toast.success(`Loaded profile "${p.name}"`);
  };

  const saveProfile = async () => {
    const name = window.prompt("Save tolerance profile as:");
    if (!name) return;
    const { standard_map, type_overrides } = mapOpts();
    try {
      await createToleranceProfile({ name, default_standard_id: standardId, categories: standard_map || {}, types: type_overrides || {} });
      toast.success(`Profile "${name}" saved`);
      listToleranceProfiles().then(setProfiles);
    } catch { toast.error("Could not save profile"); }
  };

  const removeProfile = async (id) => {
    await deleteToleranceProfile(id);
    listToleranceProfiles().then(setProfiles);
    if (profileId === id) setProfileId("none");
    toast.success("Profile removed");
  };

  const doPreview = async (nextLinks) => {
    setBusy(true);
    try {
      const res = await previewQA(design, survey, standardId, nextLinks, mapOpts());
      setQa(res);
      return res;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "QA failed");
    } finally {
      setBusy(false);
    }
  };

  const runSmartLink = async (baseRes) => {
    // baseRes = preview result computed with no links
    const missing = new Set(baseRes.result.assets.filter((a) => a.status === "MISSING").map((a) => a.asset_id));
    const extra = new Set(baseRes.result.assets.filter((a) => a.status === "EXTRA").map((a) => a.asset_id));
    const usedD = new Set(), usedS = new Set();
    const merged = [];
    let ruleCount = 0, sugCount = 0;

    // 1) saved supplier ID-mapping rules
    try {
      const r = await getLinkRules(survey);
      r.links.forEach((ln) => {
        if (missing.has(ln.design) && extra.has(ln.survey) && !usedD.has(ln.design) && !usedS.has(ln.survey)) {
          merged.push({ design: ln.design, survey: ln.survey });
          usedD.add(ln.design); usedS.add(ln.survey); ruleCount++;
        }
      });
    } catch { /* no rules */ }

    // 2) high-confidence geometry suggestions
    try {
      const s = await suggestMatches(design, survey, merged, bounds);
      s.suggestions.forEach((sug) => {
        if (sug.confidence >= threshold && !usedD.has(sug.design) && !usedS.has(sug.survey)) {
          merged.push({ design: sug.design, survey: sug.survey });
          usedD.add(sug.design); usedS.add(sug.survey); sugCount++;
        }
      });
    } catch { /* suggest failed */ }

    if (merged.length > 0) {
      setLinks(merged);
      await doPreview(merged);
    }
    setAutoNote({ ruleCount, sugCount });
    if (ruleCount || sugCount) {
      toast.success(`Auto-linked ${sugCount} by proximity, ${ruleCount} from saved supplier rules`);
    }
  };

  const run = async () => {
    if (!design || !survey || !standardId) {
      toast.error("Select a design file, survey file and a tolerance standard");
      return;
    }
    setLinks([]);
    setSelDesign(null);
    setSuggestions([]);
    setAutoNote(null);
    const res = await doPreview([]);
    if (res && autoAccept) await runSmartLink(res);
  };

  const runSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await suggestMatches(design, survey, links, bounds);
      setSuggestions(res.suggestions);
      if (res.suggestions.length === 0) toast.info("No matches found within bounds");
      else toast.success(`${res.suggestions.length} suggested association(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suggest failed");
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = async (sug) => {
    const next = [...links.filter((l) => l.design !== sug.design && l.survey !== sug.survey), { design: sug.design, survey: sug.survey }];
    setLinks(next);
    setSuggestions((s) => s.filter((x) => x.design !== sug.design && x.survey !== sug.survey));
    await doPreview(next);
  };

  const acceptAll = async () => {
    const merged = [...links];
    suggestions.forEach((sug) => {
      if (!merged.some((l) => l.design === sug.design || l.survey === sug.survey)) {
        merged.push({ design: sug.design, survey: sug.survey });
      }
    });
    setLinks(merged);
    setSuggestions([]);
    await doPreview(merged);
    toast.success("All suggestions accepted");
  };

  const dismissSuggestion = (sug) =>
    setSuggestions((s) => s.filter((x) => !(x.design === sug.design && x.survey === sug.survey)));

  const addLink = async (surveyId) => {
    if (!selDesign) return;
    const next = [...links.filter((l) => l.design !== selDesign && l.survey !== surveyId), { design: selDesign, survey: surveyId }];
    setLinks(next);
    setSelDesign(null);
    await doPreview(next);
    toast.success("Association created");
  };

  const removeLink = async (l) => {
    const next = links.filter((x) => !(x.design === l.design && x.survey === l.survey));
    setLinks(next);
    await doPreview(next);
  };

  const saveReport = async () => {
    setSaving(true);
    try {
      await runQA(design, survey, standardId, links, undefined, mapOpts());
      toast.success("Tolerance report saved to History");
    } catch {
      toast.error("Could not save report");
    } finally {
      setSaving(false);
    }
  };

  const saveRules = async () => {
    if (links.length === 0) { toast.error("Create associations first"); return; }
    setSavingRules(true);
    try {
      const r = await saveLinkRules(survey, links);
      toast.success(`Saved ${r.saved} ID-mapping rule(s) for supplier "${r.supplier}"`);
    } catch {
      toast.error("Could not save mapping rules");
    } finally {
      setSavingRules(false);
    }
  };

  const downloadPack = async () => {
    setPackBusy(true);
    try {
      await downloadSubmissionPack(
        {
          design_id: design, survey_id: survey, standard_id: standardId, links,
          ...mapOpts(),
          volume_base_surface_id: volBase !== "none" ? volBase : null,
          volume_top_surface_id: volTop !== "none" ? volTop : null,
        },
        "ADAC_Submission_Pack.pdf"
      );
      toast.success("Submission pack downloaded");
    } catch {
      toast.error("Could not generate submission pack");
    } finally {
      setPackBusy(false);
    }
  };

  const unmatchedDesign = qa ? qa.result.assets.filter((a) => a.status === "MISSING") : [];
  const unmatchedSurvey = qa ? qa.result.assets.filter((a) => a.status === "EXTRA") : [];
  const hasUnmatched = unmatchedDesign.length > 0 || unmatchedSurvey.length > 0;

  const fileSelect = (value, setter, testid, exclude) => (
    <Select value={value} onValueChange={setter}>
      <SelectTrigger data-testid={testid} className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
        <SelectValue placeholder="Select file…" />
      </SelectTrigger>
      <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
        {files.filter((f) => f.id !== exclude).map((f) => (
          <SelectItem key={f.id} value={f.id} className="font-mono text-xs">{f.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">QA Tolerance</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            Construction (design) vs as-constructed — pair assets &amp; assess tolerance
          </p>
        </div>
        {activeStd && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="view-tolerances-btn" className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300">
                <ListChecks className="w-4 h-4 mr-2" /> View Tolerances
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-zinc-800 rounded-sm max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">{activeStd.name}</DialogTitle>
                <DialogDescription className="font-mono text-xs text-zinc-500">
                  {activeStd.authority}{activeStd.datum ? ` · ${activeStd.datum}` : ""} · {activeStd.description}
                </DialogDescription>
              </DialogHeader>
              <div className="border border-zinc-800 rounded-sm overflow-hidden">
                <table className="w-full font-mono text-xs">
                  <thead>
                    <tr className="bg-[#101010] text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
                      <th className="text-left font-medium px-3 py-2">Attribute</th>
                      <th className="text-left font-medium px-3 py-2">Method</th>
                      <th className="text-right font-medium px-3 py-2">Tolerance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeStd.rules.map((r, i) => (
                      <tr key={i} className="border-b border-zinc-900 last:border-0">
                        <td className="px-3 py-2 text-zinc-200">{r.label}</td>
                        <td className="px-3 py-2 text-zinc-500">{r.method}</td>
                        <td className="px-3 py-2 text-right text-zinc-300">
                          {r.method === "exact" ? "exact match" : `±${r.value} ${r.unit}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="p-8 space-y-6">
        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr_auto] gap-4 items-end">
            <div className="space-y-1.5">
              <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Design / Construction XML</label>
              {fileSelect(design, setDesign, "design-select", survey)}
            </div>
            <div className="hidden md:flex items-center justify-center pb-2 text-zinc-600"><ArrowRight className="w-5 h-5" /></div>
            <div className="space-y-1.5">
              <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">As-Constructed / Survey XML</label>
              {fileSelect(survey, setSurvey, "survey-select", design)}
            </div>
            <div className="space-y-1.5">
              <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{perType ? "Default / fallback standard" : "Tolerance Standard (schema)"}</label>
              <Select value={standardId} onValueChange={setStandardId} disabled={lgaLocked}>
                <SelectTrigger data-testid="standard-select" className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
                  <SelectValue placeholder="Standard…" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                  {availableStandards.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="font-mono text-xs">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lgaLocked && <p className="font-mono text-[10px] text-zinc-500">Free plan — locked to your LGA schema. <a href="/settings" className="text-blue-400">Change in Setup</a> or <a href="/pricing" className="text-blue-400">subscribe</a> for all authorities.</p>}
            </div>
            <Button data-testid="run-qa-btn" onClick={run} disabled={busy} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Run QA
            </Button>
          </div>
          {files.length < 2 && (
            <p className="font-mono text-[11px] text-amber-400/80 mt-3">
              Need at least 2 documents. Load the sample dataset from the Library.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-zinc-800">
            <button
              data-testid="auto-accept-toggle"
              onClick={() => setAutoAccept((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] border transition-colors duration-75 ${autoAccept ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"}`}
            >
              <Zap className="w-3.5 h-3.5" /> Auto-accept high-confidence {autoAccept ? "ON" : "OFF"}
            </button>
            <div className="flex items-center gap-2">
              <label className="font-mono text-[11px] text-zinc-500">Confidence ≥</label>
              <input type="range" min="50" max="99" step="1" value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value))} data-testid="threshold-slider" className="w-36 accent-emerald-600" disabled={!autoAccept} />
              <span className="font-mono text-[11px] text-zinc-300 w-10" data-testid="threshold-value">{threshold}%</span>
            </div>
            <span className="font-mono text-[10px] text-zinc-600">silently links proximity matches + saved supplier ID rules on Run</span>
            <button
              data-testid="per-type-toggle"
              onClick={() => setPerType((v) => !v)}
              className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] border transition-colors duration-75 ${perType ? "border-purple-500/40 bg-purple-500/10 text-purple-400" : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"}`}
            >
              <ListChecks className="w-3.5 h-3.5" /> Per-asset-type tolerances {perType ? "ON" : "OFF"}
            </button>
          </div>
          {perType && (
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-4" data-testid="per-type-panel">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Tolerance profile</span>
                <Select value={profileId} onValueChange={applyProfile}>
                  <SelectTrigger data-testid="profile-select" className="w-56 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Load saved profile…" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                    <SelectItem value="none" className="font-mono text-[11px]">— ad-hoc (no profile) —</SelectItem>
                    {profiles.map((p) => <SelectItem key={p.id} value={p.id} className="font-mono text-[11px]">{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={saveProfile} data-testid="save-profile-btn" variant="outline" className="h-8 rounded-sm border-zinc-700 bg-transparent text-zinc-300 font-mono text-[11px]"><BookMarked className="w-3.5 h-3.5 mr-1.5" /> Save profile…</Button>
                {profileId !== "none" && (
                  <Button size="sm" onClick={() => removeProfile(profileId)} data-testid="delete-profile-btn" variant="ghost" className="h-8 w-8 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-900"><X className="w-4 h-4" /></Button>
                )}
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Category → standard</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="category-map">
                  {categories.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-zinc-300 w-28 shrink-0">{c.label}</span>
                      <Select value={catMap[c.id] || "default"} onValueChange={(v) => setCatMap((m) => ({ ...m, [c.id]: v }))}>
                        <SelectTrigger data-testid={`cat-${c.id}`} className="h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                          <SelectItem value="default" className="font-mono text-[11px]">Use default ({activeStd?.name?.split(" (")[0] || "—"})</SelectItem>
                          {standards.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {designTypes.length > 0 && (
                <div>
                  <button onClick={() => setShowOverrides((v) => !v)} data-testid="overrides-toggle" className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2 hover:text-zinc-300">
                    Per-type overrides ({Object.values(typeOverrides).filter((v) => v && v !== "default").length}) {showOverrides ? "▾" : "▸"}
                  </button>
                  {showOverrides && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="type-overrides">
                      {designTypes.map((t) => (
                        <div key={t} className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-zinc-400 w-32 shrink-0 truncate" title={t}>{t}</span>
                          <Select value={typeOverrides[t] || "default"} onValueChange={(v) => setTypeOverrides((m) => ({ ...m, [t]: v }))}>
                            <SelectTrigger data-testid={`type-${t}`} className="h-7 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                              <SelectItem value="default" className="font-mono text-[10px]">Use category default</SelectItem>
                              {standards.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[10px]">{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="font-mono text-[10px] text-zinc-600">Each asset is auto-classified by keyword (e.g. "Sewer Maintenance Hole" → Sewer). A category falls back to the default standard unless mapped. An exact-type override beats its category.</p>
            </div>
          )}
        </div>

        {/* Manual association panel */}
        {qa && (hasUnmatched || links.length > 0) && (
          <div className="border border-blue-600/30 bg-blue-600/[0.04] rounded-sm p-5" data-testid="link-panel">
            <div className="flex items-center gap-2 mb-1">
              <Link2 className="w-4 h-4 text-blue-400" />
              <h3 className="font-display text-sm font-semibold text-blue-300">Associate unmatched assets</h3>
            </div>
            <p className="font-mono text-[11px] text-zinc-500 mb-4">
              Auto-linking couldn't pair these by ObjectId. Use <b>Suggest matches</b> (geometry-aware,
              nearest same-type within bounds), or click a design asset then its survey match.
            </p>

            {/* Suggest matches (C -> B) */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-sm border border-zinc-800 bg-[#0C0C0C]">
              <Button
                data-testid="suggest-btn"
                onClick={runSuggest}
                disabled={suggesting}
                className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white h-9"
              >
                {suggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                Suggest matches
              </Button>
              <div className="flex items-center gap-2">
                <label className="font-mono text-[11px] text-zinc-500">Bounds</label>
                <input
                  type="range"
                  min="0.1"
                  max="30"
                  step="0.1"
                  value={bounds}
                  onChange={(e) => setBounds(parseFloat(e.target.value))}
                  data-testid="bounds-slider"
                  className="w-40 accent-blue-600"
                />
                <span className="font-mono text-[11px] text-zinc-300 w-16" data-testid="bounds-value">{bounds.toFixed(1)} m</span>
              </div>
              <span className="font-mono text-[10px] text-zinc-600">order: polygons → lines → points, then nearest same-type</span>
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  size="sm"
                  data-testid="map-link-toggle"
                  variant="outline"
                  onClick={() => setMapLink((v) => !v)}
                  className={`h-9 rounded-sm border-zinc-700 bg-transparent ${mapLink ? "text-blue-400 border-blue-500/40" : "text-zinc-300"}`}
                >
                  <MapPin className="w-3.5 h-3.5 mr-1.5" /> Link on map
                </Button>
                <Button
                  size="sm"
                  data-testid="save-rules-btn"
                  onClick={saveRules}
                  disabled={savingRules || links.length === 0}
                  className="h-9 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
                >
                  {savingRules ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BookMarked className="w-3.5 h-3.5 mr-1.5" />} Save supplier rules
                </Button>
              </div>
            </div>

            {autoNote && (autoNote.ruleCount > 0 || autoNote.sugCount > 0) && (
              <div className="mb-4 font-mono text-[11px] text-emerald-400/90 flex items-center gap-2" data-testid="auto-note">
                <Zap className="w-3.5 h-3.5" /> Auto-linked {autoNote.sugCount} by proximity and {autoNote.ruleCount} from saved supplier ID rules. Review or undo below.
              </div>
            )}

            {mapLink && (
              <MapLink
                design={design}
                survey={survey}
                unmatchedDesign={unmatchedDesign}
                unmatchedSurvey={unmatchedSurvey}
                selDesign={selDesign}
                setSelDesign={setSelDesign}
                onLink={addLink}
              />
            )}

            {suggestions.length > 0 && (
              <div className="mb-4 border border-blue-600/30 rounded-sm overflow-hidden" data-testid="suggestions">
                <div className="flex items-center justify-between px-3 py-2 bg-blue-600/10 border-b border-blue-600/20">
                  <span className="font-mono text-[11px] text-blue-300">{suggestions.length} suggested association(s)</span>
                  <Button size="sm" data-testid="accept-all-btn" onClick={acceptAll} className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
                    <Check className="w-3.5 h-3.5 mr-1" /> Accept all
                  </Button>
                </div>
                <div className="divide-y divide-zinc-900">
                  {suggestions.map((sug, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5" data-testid={`suggestion-${sug.design}`}>
                      <div className="font-mono text-xs flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-200">{sug.design}</span>
                        <ArrowRight className="w-3 h-3 text-zinc-600" />
                        <span className="text-zinc-200">{sug.survey}</span>
                        <span className="text-zinc-500">· {sug.design_type} · {sug.geometry}</span>
                        {sug.distance_m != null && <span className="text-zinc-500">· {sug.distance_m} m</span>}
                        <span className="px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[10px]">{sug.confidence}%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" data-testid={`accept-${sug.design}`} onClick={() => acceptSuggestion(sug)} className="h-7 rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
                          <Check className="w-3.5 h-3.5 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismissSuggestion(sug)} className="h-7 w-7 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UnmatchedColumn
                title={`Design — unmatched (${unmatchedDesign.length})`}
                items={unmatchedDesign}
                side="design"
                selected={selDesign}
                onClick={(a) => setSelDesign(selDesign === a.asset_id ? null : a.asset_id)}
                testid="unmatched-design"
              />
              <UnmatchedColumn
                title={`Survey — unmatched (${unmatchedSurvey.length})`}
                items={unmatchedSurvey}
                side="survey"
                disabled={!selDesign}
                onClick={(a) => addLink(a.asset_id)}
                testid="unmatched-survey"
              />
            </div>
            {selDesign && (
              <p className="font-mono text-[11px] text-blue-400 mt-3">
                Selected design <b>{selDesign}</b> — now click a survey asset to link it.
              </p>
            )}
            {links.length > 0 && (
              <div className="mt-4 border-t border-zinc-800 pt-4">
                <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                  Manual associations ({links.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {links.map((l, i) => (
                    <span key={i} className="inline-flex items-center gap-2 font-mono text-[11px] px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-700 text-zinc-300" data-testid={`link-chip-${l.design}`}>
                      <LinkIcon className="w-3 h-3 text-blue-400" />
                      {l.design} → {l.survey}
                      <button onClick={() => removeLink(l)} className="text-zinc-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {qa ? (
          <>
            <div className="flex flex-wrap justify-end items-center gap-2" data-testid="pack-controls">
              {surfaces.length > 0 && (
                <div className="flex items-center gap-2 mr-auto" data-testid="pack-volume-selectors">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Pack volume (optional)</span>
                  <Select value={volBase} onValueChange={setVolBase}>
                    <SelectTrigger data-testid="pack-vol-base" className="w-40 h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Base surface…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                      <SelectItem value="none" className="font-mono text-[11px]">No base</SelectItem>
                      {surfaces.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name} ({s.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={volTop} onValueChange={setVolTop}>
                    <SelectTrigger data-testid="pack-vol-top" className="w-40 h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Top surface…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                      <SelectItem value="none" className="font-mono text-[11px]">No top</SelectItem>
                      {surfaces.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name} ({s.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button data-testid="submission-pack-btn" onClick={downloadPack} disabled={packBusy} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
                {packBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                Submission Pack PDF
              </Button>
              <Button data-testid="save-qa-btn" onClick={saveReport} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Report
              </Button>
            </div>
            <QAResult qa={qa} />
          </>
        ) : (
          <div className="grid-texture border border-zinc-800 rounded-sm py-20 flex flex-col items-center text-center">
            <ShieldCheck className="w-10 h-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500 max-w-md">
              Select the construction (design) and as-constructed (survey) XML for the same project,
              pick a tolerance standard, and run QA. Unmatched assets can be linked manually.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function UnmatchedColumn({ title, items, side, selected, disabled, onClick, testid }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C]" data-testid={testid}>
      <div className="px-3 py-2 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
        {title}
      </div>
      <div className="max-h-72 overflow-auto p-2 space-y-1">
        {items.length === 0 ? (
          <p className="font-mono text-[11px] text-zinc-600 p-2">All linked ✓</p>
        ) : (
          items.map((a) => {
            const isSel = selected === a.asset_id;
            return (
              <button
                key={a.asset_id}
                data-testid={`${side}-item-${a.asset_id}`}
                onClick={() => onClick(a)}
                disabled={disabled}
                className={`w-full text-left px-3 py-2 rounded-sm border transition-colors duration-75 ${
                  isSel
                    ? "border-blue-500 bg-blue-600/15"
                    : disabled
                    ? "border-zinc-900 bg-zinc-950/40 opacity-50 cursor-not-allowed"
                    : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600 hover:bg-zinc-900"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-zinc-200">{a.asset_id}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{a.geometry}</span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{a.type}</div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}


function MapLink({ unmatchedDesign, unmatchedSurvey, selDesign, setSelDesign, onLink }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layer = useRef(null);
  const [zone, setZone] = useState(56);

  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView([-24.87, 152.35], 13);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }).addTo(map);
    layer.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 200);
  }, []);

  useEffect(() => {
    if (!mapObj.current || !layer.current) return;
    layer.current.clearLayers();
    const def = mgaDef(zone);
    const pts = [];
    const plot = (items, side) => {
      items.forEach((a) => {
        const e = parseFloat(a.fields?.Easting_m), n = parseFloat(a.fields?.Northing_m);
        if (isNaN(e) || isNaN(n)) return;
        const [lon, lat] = proj4(def, proj4.WGS84, [e, n]);
        const isSel = side === "design" && selDesign === a.asset_id;
        const color = side === "design" ? (isSel ? "#3b82f6" : "#f59e0b") : "#22d3ee";
        const m = L.circleMarker([lat, lon], { radius: isSel ? 10 : 7, color: "#fff", weight: 2, fillColor: color, fillOpacity: 0.95 });
        m.bindTooltip(`${a.asset_id} · ${side}`, { permanent: false });
        m.on("click", () => { side === "design" ? setSelDesign(selDesign === a.asset_id ? null : a.asset_id) : (selDesign ? onLink(a.asset_id) : null); });
        m.addTo(layer.current);
        pts.push([lat, lon]);
      });
    };
    plot(unmatchedDesign, "design");
    plot(unmatchedSurvey, "survey");
    if (pts.length) mapObj.current.fitBounds(pts, { padding: [50, 50], maxZoom: 18 });
  }, [unmatchedDesign, unmatchedSurvey, selDesign, zone, onLink, setSelDesign]);

  return (
    <div className="mb-4 border border-blue-600/30 rounded-sm overflow-hidden" data-testid="map-link-panel">
      <div className="flex items-center justify-between px-3 py-2 bg-blue-600/10 border-b border-blue-600/20">
        <span className="font-mono text-[11px] text-blue-300">
          Click an <span className="text-amber-400">amber design</span> marker then a <span className="text-cyan-400">cyan survey</span> marker to link
        </span>
        <Select value={String(zone)} onValueChange={(v) => setZone(parseInt(v))}>
          <SelectTrigger data-testid="maplink-zone" className="w-28 h-7 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
            {[54, 55, 56].map((z) => <SelectItem key={z} value={String(z)} className="font-mono text-xs">MGA Zone {z}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div ref={mapRef} data-testid="map-link-canvas" style={{ height: "340px", background: "#e5e7eb" }} />
    </div>
  );
}
